import { useRouter } from 'next/router'
import { baseApi } from 'services'

import { useAppSelector } from '@/client/hooks'
import { UpdateApplicantForm, UpdateApplicantProfileForm } from '@/types/application'
import { DealTeam } from '@/types/global'
import { CreateIssueForm, Issue } from '@/types/issues'
import {
  Address,
  Application,
  CheckListItemSequenceNo,
  ChecklistItem,
  IConsts,
  KickBackData,
  PaginatedResults,
  Party,
  Property,
  Sharepoint,
  WorkQueueState,
  WorkQueuesField,
} from '@/types/unlock'

export const applicationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listRecentApplications: builder.query<Application[], void>({
      query: () => ({
        url: 'v1/application/',
        params: {
          fields: 'property,created_at,modified_at,external_id,uuid',
          mode: 'recent',
        },
      }),
    }),
    searchApplications: builder.query<Application[], string>({
      query: (search) => `v1/application?mode=search&search=${search}`,
    }),
    listLastViewedApplications: builder.query<Application[], string[]>({
      query: (ids) => `v1/application?mode=last_viewed&ids=${ids}`,
    }),
    getApplicationById: builder.query<Application, string | string[]>({
      query: (id) => `v1/application/${id}/`,
      providesTags: () => ['Application'],
    }),
    getSharepointUrl: builder.query<Sharepoint, string | string[]>({
      query: (id) => `v1/sharepoint/?application_id=${id}`,
    }),
    updateApplicantProfile: builder.mutation<Party, UpdateApplicantProfileForm>({
      query: ({ uuid, ...patch }) => ({
        url: `v1/application-profile/${uuid}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationProfiles',
        'ApplicationGuidelines',
        'ApplicationParties',
      ],
    }),
    getApplicationSsns: builder.query<object, string | string[]>({
      query: (id) => `v1/application/${id}/get-application-ssns/`,
      providesTags: ['ApplicationProfiles', 'ApplicationParties'],
    }),
    getApplicationSsnsV2: builder.query<object, string | string[]>({
      query: (uuid) => `v2/application/${uuid}/get-application-ssns/`,
      providesTags: ['ApplicationProfiles', 'ApplicationParties'],
    }),
    updateApplicant: builder.mutation<Party, UpdateApplicantForm>({
      query: ({ id, ...patch }) => ({
        url: `v2/profiles/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationProfiles',
        'ApplicationGuidelines',
        'ApplicationParties',
        'MaxActions',
        'Alert',
      ],
    }),
    createIssue: builder.mutation<Issue, CreateIssueForm>({
      query: (issue) => ({
        url: 'v2/issues/',
        method: 'POST',
        body: issue,
      }),
    }),
    updateApplication: builder.mutation<
      Application,
      Partial<Application> & { sub_loss_reason_other_text?: string }
    >({
      query: (application) => ({
        url: `v2/application/${application.external_id}/`,
        method: 'PATCH',
        body: application,
      }),
      invalidatesTags: [
        'Application',
        'AutomatedApplicationExceptions',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ApplicationCalcs',
        'ChecklistGroups',
        'MaxActions',
        'Alert',
      ],
    }),
    updateSubStage: builder.mutation<
      Application,
      Partial<Application> & { kick_back_data?: KickBackData } & { aan_denial_reasons?: string[] }
    >({
      query: (application) => ({
        url: `v2/application/${application.external_id}/update-substage/`,
        method: 'PATCH',
        body: application,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationCalcs',
        'Escalations',
        'AutomatedApplicationExceptions',
        'StreamingNote',
        'Alert',
        'ChecklistGroups',
      ],
    }),
    updateProperty: builder.mutation<Property, Partial<Property>>({
      query: (property) => ({
        url: `v2/properties/${property.uuid}/`,
        method: 'PATCH',
        body: property,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ApplicationProfiles',
        'ChecklistGroups',
        'Alert',
        'StreamingNote',
      ],
    }),
    updatePropertyAddress: builder.mutation<Address, Partial<Address>>({
      query: (address) => ({
        url: `v2/address/${address.uuid}/`,
        method: 'PATCH',
        body: address,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ApplicationCalcs',
        'AutomatedApplicationExceptions',
        'MaxActions',
      ],
    }),
    importApplicationData: builder.mutation<object, any>({
      query: ({ destination_application, source_application }) => ({
        url: `v2/application/${destination_application}/import-data/`,
        method: 'POST',
        body: { source_application },
      }),
      invalidatesTags: ['Application', 'ApplicationCalcs'],
    }),
    createChecklistItem: builder.mutation<ChecklistItem, Partial<ChecklistItem>>({
      query: (checklistItem) => ({
        url: `v2/checklist-items/`,
        method: 'POST',
        body: checklistItem,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ChecklistGroups',
      ],
    }),
    updateChecklistItem: builder.mutation<ChecklistItem, Partial<ChecklistItem>>({
      query: (checklistItem) => ({
        url: `v2/checklist-items/${checklistItem.uuid}/`,
        method: 'PATCH',
        body: checklistItem,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ChecklistGroups',
      ],
    }),
    updateTaskAudit: builder.mutation<ChecklistItem, Partial<ChecklistItem>>({
      query: (checklistItem) => ({
        url: `v2/checklist-items/${checklistItem.uuid}/update-task-audit/`,
        method: 'PATCH',
        body: checklistItem,
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ChecklistGroups',
      ],
    }),
    updateCheckListItemSequenceNo: builder.mutation<
      CheckListItemSequenceNo,
      CheckListItemSequenceNo
    >({
      query: (checkListItemSequenceNo) => ({
        url: `v2/checklist-items/${checkListItemSequenceNo.uuid_from}/change-sequence-no/`,
        method: 'PATCH',
        body: { destination_index: checkListItemSequenceNo.destination_index },
      }),
      invalidatesTags: ['Application', 'ApplicationOffer', 'ApplicationGuidelines'],
    }),
    deleteChecklistItem: builder.mutation<ChecklistItem, string>({
      query: (uuid) => ({
        url: `v2/checklist-items/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'Application',
        'ApplicationOffer',
        'ApplicationGuidelines',
        'ChecklistGroups',
      ],
    }),
    getWorkQueuesCount: builder.query<WorkQueueState[], string | void>({
      query: (subscribers) => {
        return {
          url: `v2/application/count/`,
          params: !subscribers ? null : { subscribers },
        }
      },
      providesTags: ['ApplicationCount'],
    }),
    getWorkQueuesApplications: builder.query<
      PaginatedResults<Partial<Application>[]>,
      {
        applicationState?: string
        page?: number
        pageSize?: number
        search?: string
        subscribers?: string
        ordering?: { field: string; ascending: number }
        fields?: string[]
      }
    >({
      query: (arg) => {
        const {
          applicationState,
          page = 1,
          pageSize = 50,
          search,
          subscribers,
          ordering,
          fields,
        } = arg

        const requestedFields = fields || [
          'uuid',
          'name',
          'delay_reasons',
          'application_state_modified_at',
          'estimated_close_on',
          'modified_at',
          'subscribers',
          'id',
          'external_id',
          'application_state',
          'application_state_label',
          'property',
          'uw_submitted_at',
          'hubspot_contact_id',
        ]

        return {
          url: `v2/application/`,
          method: 'GET',
          params: {
            application_state: applicationState,
            fields: requestedFields,
            page: page,
            page_size: pageSize,
            name: search || '',
            subscribers__user__in: subscribers,
            order_by: (ordering?.ascending === -1 ? '-' : '') + ordering?.field,
          },
        }
      },
      providesTags: ['ApplicationWorkQueues'],
    }),
    // Specific query for the work queues page, uses specific endpoint
    // that joins data, calcs, and exceptions.
    // See PipelineApplicationViewSet in the backend for reference
    listPipelineApplications: builder.query<
      PaginatedResults<any[]>,
      {
        applicationState?: string
        page?: number
        pageSize?: number
        search?: string
        subscribers?: string
        ordering?: { field: string; ascending: number }
        fields?: string[]
      }
    >({
      query: (arg) => {
        const { applicationState, page = 1, pageSize = 50, search, subscribers, ordering } = arg

        return {
          url: `v2/pipeline-applications/`,
          method: 'GET',
          params: {
            application_state: applicationState,
            page: page,
            page_size: pageSize,
            name: search || '',
            subscribers__user__in: subscribers,
            order_by: (ordering?.ascending === -1 ? '-' : '') + ordering?.field,
          },
        }
      },
      providesTags: ['PipelineApplications'],
    }),

    getWorkQueues: builder.query<{ results: Application[]; count: number }, unknown>({
      query: (params) => ({ url: `v2/work-queues/`, params }),
    }),

    upsertDealTeam: builder.mutation<unknown, Partial<DealTeam>>({
      query: (body) => ({
        url: body.id ? `v2/deal-team/${body.id}/` : `v2/deal-team/`,
        method: body.id ? 'PUT' : 'POST',
        body,
      }),
      invalidatesTags: ['DealTeam'],
    }),

    deleteDealTeam: builder.mutation<unknown, number>({
      query: (dealTeamId) => ({
        url: `v2/deal-team/${dealTeamId}/`,
        method: 'DELETE',
      }),
    }),

    getDealTeamsByApplication: builder.query<PaginatedResults<DealTeam[]>, number>({
      query: (applicationId) => `v2/deal-team/?application=${applicationId}`,
      providesTags: ['DealTeam'],
    }),

    getWorkQueuesFields: builder.query<WorkQueuesField[], void>({
      query: () => ({
        url: 'v2/work-queues/fields',
      }),
    }),
    updateUseOfProceeds: builder.mutation<
      void,
      {
        external_id: number
        use_of_proceeds: Array<{
          application: number
          name: string
        }>
      }
    >({
      query: (useOfProceedsForm) => ({
        url: `v1/application/${useOfProceedsForm.external_id}/update-use-of-proceeds/`,
        method: 'PATCH',
        body: { use_of_proceeds: useOfProceedsForm.use_of_proceeds },
      }),
      invalidatesTags: ['UseOfProceeds', 'Alert'],
    }),

    getAuditCheckSummary: builder.query<any, string | string[]>({
      query: (applicationId) => ({
        url: `v2/audit-check-summary/${applicationId}/`,
        method: 'POST',
      }),
    }),

    getAggregatedField: builder.query<any, any>({
      query: (params) => ({
        url: `v2/work-queues/agg-field/`,
        method: 'GET',
        params,
      }),
    }),
    getLatestPricingVersion: builder.query<{ pricing_version: number }, void>({
      query: () => {
        return {
          url: `v2/application/latest-pricing-version/`,
        }
      },
      providesTags: ['ApplicationPricingVersion'],
    }),
    getConsts: builder.query<IConsts, void>({
      query: () => '/v1/customer/consts/',
      providesTags: ['Consts'],
    }),
  }),
})

export const useCurrentApplication = () => {
  const router = useRouter()

  const pipelineSelectedAppId = useAppSelector((state) => state.workQueues.selectedAppId)

  const adminSelectedAppId = useAppSelector((state) => state.fileManager.adminSelectedAppId)

  const id =
    router.route.includes('work-queues') || router.route.includes('work-views')
      ? pipelineSelectedAppId
      : router.route.includes('admin')
        ? adminSelectedAppId
        : (router.query.id as string)

  return useGetApplicationByIdQuery(id, {
    skip: !id,
  })
}

export const {
  useListRecentApplicationsQuery,
  useSearchApplicationsQuery,
  useListLastViewedApplicationsQuery,
  useGetApplicationByIdQuery,
  useGetApplicationSsnsQuery,
  useGetApplicationSsnsV2Query,
  useGetSharepointUrlQuery,
  useLazyGetApplicationByIdQuery,
  useUpdateApplicantMutation,
  useUpdateApplicantProfileMutation,
  useCreateIssueMutation,
  useUpdateApplicationMutation,
  useUpdateSubStageMutation,
  useUpdatePropertyMutation,
  useUpdatePropertyAddressMutation,
  useUpdateChecklistItemMutation,
  useUpdateTaskAuditMutation,
  useUpdateCheckListItemSequenceNoMutation,
  useImportApplicationDataMutation,
  useCreateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useGetWorkQueuesCountQuery,
  useGetWorkQueuesApplicationsQuery,
  useUpdateUseOfProceedsMutation,
  useListPipelineApplicationsQuery,
  useLazyGetWorkQueuesQuery,
  useDeleteDealTeamMutation,
  useGetWorkQueuesFieldsQuery,
  useGetDealTeamsByApplicationQuery,
  useUpsertDealTeamMutation,
  useGetAuditCheckSummaryQuery,
  useLazyGetAggregatedFieldQuery,
  useGetLatestPricingVersionQuery,
  useGetConstsQuery,
} = applicationApi
